import { linearGradientDef } from '@nivo/core';
import { ResponsiveLine } from '@nivo/line';
import { Serie } from '@nivo/line';
import React, { useMemo } from 'react';
import { CopilotLineGraphBackgroundLayer } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphBackgroundLayer';
import { PoolPointData } from 'apps/sigma2-ui/src/copilot/components/PoolPointData';
import { EmployeeRawCurvePointData } from 'apps/sigma2-ui/src/copilot/components/PoolPointData';
import { TooltipPosition } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipPositioning';
import { determineTooltipPosition } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipPositioning';
import { CopilotCustomMeshLayer } from 'apps/sigma2-ui/src/copilot/components/CopilotCustomMeshLayer';
import { LegendLineGroup } from 'apps/sigma2-ui/src/components/svg/LegendLine';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { ClusteredDataPoint } from 'libs/end-to-end/src/copilot';
import { SlateCopilotDataPoint } from 'libs/end-to-end/src/copilot';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';

const gaussianKernel = (u: number) =>
  (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * u * u);

export const makeData = (
  dataPoints: SlateCopilotDataPoint[],
  clusteredDataPoints: ClusteredDataPoint[],
  currentlySelectedSubject: SlatePoolSubject | null,
): Serie[] => {
  const rawCurveSeriesData: EmployeeRawCurvePointData[] = [];
  const poolSeriesData: PoolPointData[] = [];

  for (const dataPoint of dataPoints) {
    let totalWeight = 0;
    let sumWeightedY = 0;
    for (const innerPoint of dataPoints) {
      const weight = gaussianKernel(
        (innerPoint.readinessRating - dataPoint.readinessRating) / 2,
      );
      sumWeightedY += innerPoint.numberOfEmployees * weight;
      totalWeight += weight;
    }

    const rawCurveData: EmployeeRawCurvePointData = {
      x: dataPoint.readinessRating,
      y: sumWeightedY / totalWeight,
      metadata: {
        type: 'raw_curve',
      },
      // y: dataPoint.numberOfEmployees,
    };
    rawCurveSeriesData.push(rawCurveData);
  }

  for (const cluster of clusteredDataPoints) {
    const {
      numberOfSubjects,
      minimumReadiness,
      maximumReadiness,
      averageReadinessWhole,
    } = cluster;

    const poolData: PoolPointData = {
      x: averageReadinessWhole,
      y:
        rawCurveSeriesData.find((point) => point.x === averageReadinessWhole)
          ?.y ??
        rawCurveSeriesData.at(-1)?.y ??
        0,
      metadata: {
        type: 'pool',
        minimumReadiness: minimumReadiness,
        maximumReadiness: maximumReadiness,
        isSelected:
          currentlySelectedSubject !== null &&
          currentlySelectedSubject.readinessRating >= minimumReadiness &&
          currentlySelectedSubject.readinessRating <= maximumReadiness,
        subjectsCount: numberOfSubjects,
      },
    };

    poolSeriesData.push(poolData);
  }

  return [
    {
      id: 'series_employee_curve',
      data: rawCurveSeriesData,
    },
    {
      id: 'series_pool',
      data: poolSeriesData,
    },
  ];
};

interface MinMax {
  min: number;
  max: number;
}

export interface CopilotLineGraphProps {
  onClick: (
    toolTipPosition: TooltipPosition,
    minReadiness: number,
    maxReadiness: number,
  ) => void;
  dataPoints: SlateCopilotDataPoint[];
  clusteredDataPoints: ClusteredDataPoint[];
  currentlySelectedSubject: SlatePoolSubject | null;
  children?: React.ReactNode;
}

export const CopilotLineGraph = (props: CopilotLineGraphProps) => {
  const seriesData = useMemo<Serie[]>(() => {
    return makeData(
      props.dataPoints,
      props.clusteredDataPoints,
      props.currentlySelectedSubject,
    );
  }, [
    props.clusteredDataPoints,
    props.currentlySelectedSubject,
    props.dataPoints,
  ]);

  const yMinMax = useMemo<MinMax>(() => {
    let min = 0;
    let max = 0;
    for (const series of seriesData) {
      for (const point of series.data) {
        if ((point.y as number) < min) {
          min = point.y as number;
        }
        if ((point.y as number) > max) {
          max = point.y as number;
        }
      }
    }

    // Create a buffer of 10% of the difference between min and max
    const diff = max - min;
    const buffer = diff * 0.05;

    return {
      min: min - buffer,
      max: max + buffer,
    };
  }, [seriesData]);

  const minX = 0;
  const maxX = 105;

  const clusterMinBaseSize = 40;
  const clusterMaxGrowSize = 20;

  const clusterSmallestGroupSize =
    props.clusteredDataPoints.reduce(
      (smallestGroupSize: number | null, cluster) => {
        if (
          smallestGroupSize === null ||
          cluster.numberOfSubjects < smallestGroupSize
        ) {
          return cluster.numberOfSubjects;
        } else {
          return smallestGroupSize;
        }
      },
      null,
    ) ?? 0;

  const clusterLargestGroupSize =
    props.clusteredDataPoints.reduce(
      (largestGroupSize: number | null, cluster) => {
        if (
          largestGroupSize === null ||
          cluster.numberOfSubjects > largestGroupSize
        ) {
          return cluster.numberOfSubjects;
        } else {
          return largestGroupSize;
        }
      },
      null,
    ) ?? 0;

  const clusterSizeRange = clusterLargestGroupSize - clusterSmallestGroupSize;

  return (
    <>
      {props.children && (
        <div
          className={
            'absolute z-cover top-0 left-0 w-full h-full bg-transparent'
          }
        >
          {props.children}
        </div>
      )}
      <div className={'relative w-full h-full'}>
        <ResponsiveLine
          data={seriesData}
          defs={[
            linearGradientDef('lineGradient', [
              { offset: 0, color: '#79BFFF' },
              { offset: 100, color: '#364FD4' },
            ]),
          ]}
          fill={[{ match: { id: 'line' }, id: 'gradientA' }]}
          layers={[
            CopilotLineGraphBackgroundLayer,
            'grid',
            'markers',
            'axes',
            'areas',
            'crosshair',
            'lines',
            'points',
            'slices',
            CopilotCustomMeshLayer,
            'legends',
          ]}
          margin={{ top: 20, right: 5, bottom: 0, left: 40 }}
          xScale={{
            type: 'linear',
            min: minX,
            max: maxX,
            stacked: false,
            reverse: false,
          }}
          colors={(datum: Serie) => {
            if (datum.id === 'series_employee_curve') {
              return 'url(#lineGradient)';
            } else {
              return '';
            }
          }}
          theme={{
            grid: {
              line: {
                stroke: '#FFFFFF',
              },
            },
            crosshair: {
              line: {
                stroke: '#FFFFFF',
              },
            },
            text: {
              color: '#FFFFFF',
            },
            axis: {
              legend: {
                text: {
                  fontFamily: KARLA_FONT_FAMILY,
                },
              },
            },
          }}
          yScale={{
            type: 'linear',
            min: yMinMax.min,
            max: yMinMax.max,
            stacked: false,
            reverse: false,
          }}
          curve="monotoneX"
          yFormat=" >-.2f"
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={{
            legend: '',
            legendPosition: 'middle',
            legendOffset: -20,
            tickSize: 0,
            tickValues: [],
          }}
          enableGridX={false}
          enableGridY={false}
          enablePointLabel={true}
          pointSymbol={(props) => {
            const baseColor = '#3661e2';
            const secondaryColor = '#c9d5f8';
            const pointData = props.datum as
              | PoolPointData
              | EmployeeRawCurvePointData;
            if (pointData.metadata.type === 'raw_curve') {
              return null;
            }

            if (pointData.metadata.subjectsCount === 0) {
              return null;
            }

            const fillColor = !pointData.metadata.isSelected
              ? baseColor
              : secondaryColor;
            const strokeColor = !pointData.metadata.isSelected
              ? secondaryColor
              : baseColor;

            const numberOfSubjects = pointData.metadata.subjectsCount;
            const amountBeyondMinimum =
              numberOfSubjects - clusterSmallestGroupSize;

            const baseSize =
              clusterSizeRange === 0
                ? clusterMinBaseSize + clusterMaxGrowSize / 2
                : clusterMinBaseSize +
                  clusterMaxGrowSize * (amountBeyondMinimum / clusterSizeRange);

            const xAndY = baseSize / 2;
            const xAndYNegative = baseSize / -2;

            const fontSize = baseSize / 4;

            return (
              <svg
                width={baseSize}
                height={baseSize}
                x={xAndYNegative}
                y={xAndYNegative}
                viewBox={`0 0 ${baseSize} ${baseSize}`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <circle
                    cx={xAndY}
                    cy={xAndY}
                    r={xAndY / 2}
                    fill={fillColor}
                    stroke={strokeColor}
                    strokeWidth={1}
                  />
                  {numberOfSubjects > 1 && (
                    <text
                      x="50%"
                      y="50%"
                      dominantBaseline="middle"
                      textAnchor="middle"
                      fill={strokeColor}
                      fontSize={`${fontSize}px`}
                    >
                      {numberOfSubjects}
                    </text>
                  )}
                </g>
              </svg>
            );
          }}
          pointLabel={() => {
            return '';
          }}
          isInteractive={true}
          useMesh={false}
          tooltip={() => {
            return null;
          }}
          onClick={(point) => {
            const poolData = point.data as
              | PoolPointData
              | EmployeeRawCurvePointData;
            if (poolData.metadata.type === 'raw_curve') {
              return null;
            }
            const tooltipPosition = determineTooltipPosition({
              yMax: yMinMax.max,
              yMin: yMinMax.min,
              xMax: maxX,
              xMin: minX,
              data: poolData as PoolPointData,
            });
            props.onClick(
              tooltipPosition,
              poolData.metadata.minimumReadiness,
              poolData.metadata.maximumReadiness,
            );
          }}
        />
        <div className="absolute bottom-0 left-0 h-full w-[20px] bg-blue-1000">
          <div className={'flex flex-col h-full justify-center'}>
            <RotatedPart />
          </div>
        </div>
      </div>
    </>
  );
};

const RotatedPart = () => {
  return (
    <svg width="25" height="210" viewBox="0 0 25 210">
      <g transform="rotate(-90) translate(-210 15)">
        <g transform="translate(0 -4)">
          <LegendLineGroup />
        </g>
        <text
          x={28}
          y={0}
          fill={'#FFFFFF'}
          style={{
            fontSize: '12px',
            fontFamily: KARLA_FONT_FAMILY,
          }}
        >
          Number of Current Employees
        </text>
      </g>
    </svg>
  );
};
